import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

// Icons
import { Building, ArrowRight, ChevronRight, ChevronLeft, Save, Eye, EyeOff, Check, CreditCard, Sparkles } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";

// Steps for registration
enum RegistrationStep {
	OrganizationDetails = 0,
	Pricing = 1,
	UserDetails = 2,
}

const RegisterPage = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { user } = useAuth();
	const [currentStep, setCurrentStep] = useState<RegistrationStep>(RegistrationStep.OrganizationDetails);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [attentionManuallyEdited, setAttentionManuallyEdited] = useState(false);
	const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year');
	const [selectedPlan, setSelectedPlan] = useState<'tier1' | 'tier2'>('tier1');
	
	// Redirect to home if user is already authenticated
	useEffect(() => {
		if (user) {
			navigate("/");
		}
	}, [user, navigate]);
	
	// Organization details form state
	const [organizationForm, setOrganizationForm] = useState({
		name: "",
		vatNumber: "",
		invoiceEmail: "",
		attention: "",
		street: "",
		city: "",
		zip: "",
		country: "Belgium",
	});
	
	// User details form state
	const [userForm, setUserForm] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		confirmPassword: "",
		acceptTerms: false,
	});
	
	// Password validation criteria
	const hasLowercase = /[a-z]/.test(userForm.password);
	const hasUppercase = /[A-Z]/.test(userForm.password);
	const hasDigit = /[0-9]/.test(userForm.password);
	const hasMinLength = userForm.password.length >= 6;
	const isPasswordValid = hasLowercase && hasUppercase && hasDigit && hasMinLength;
	
	// Add password match validation
	const passwordsMatch = userForm.password === userForm.confirmPassword;
	
	// Handle organization form changes
	const handleOrgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setOrganizationForm(prev => ({
			...prev,
			[name]: value
		}));
		
		// If changing organization name, update attention field unless manually edited
		if (name === "name") {
			if (!attentionManuallyEdited) {
				setOrganizationForm(prev => ({
					...prev,
					attention: value
				}));
			}
		}
		
		// If changing the attention field, mark it as manually edited
		if (name === "attention") {
			setAttentionManuallyEdited(true);
		}
	};
	
	// Handle country selection
	const handleCountryChange = (value: string) => {
		setOrganizationForm(prev => ({
			...prev,
			country: value
		}));
	};
	
	// Handle user form changes
	const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setUserForm(prev => ({
			...prev,
			[name]: value
		}));
	};
	
	// Toggle password visibility
	const togglePasswordVisibility = () => {
		setShowPassword(prev => !prev);
	};
	
	// Handle terms and conditions checkbox
	const handleTermsChange = (checked: boolean) => {
		setUserForm(prev => ({
			...prev,
			acceptTerms: checked
		}));
	};
	
	// Go to next step
	const handleNextStep = () => {
		if (currentStep === RegistrationStep.OrganizationDetails) {
			// Validate organization form
			if (!organizationForm.name) {
				toast.error("Organization name is required");
				return;
			}
			if (!organizationForm.invoiceEmail) {
				toast.error("Invoice email is required");
				return;
			}
			if (!organizationForm.street || !organizationForm.city || !organizationForm.zip) {
				toast.error("Address details are required");
				return;
			}
		}
		
		setCurrentStep(prev => prev + 1);
	};
	
	// Go to previous step
	const handlePrevStep = () => {
		setCurrentStep(prev => prev - 1);
	};
	
	// Submit the registration form
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		// Validate user form
		if (!userForm.firstName || !userForm.lastName) {
			toast.error("First name and last name are required");
			return;
		}
		
		if (!userForm.email) {
			toast.error("Email address is required");
			return;
		}
		
		if (!isPasswordValid) {
			toast.error("Password doesn't meet the requirements");
			return;
		}

		if (!passwordsMatch) {
			toast.error(t("register.user.passwordsDoNotMatch") || "Passwords do not match");
			return;
		}
		
		if (!userForm.acceptTerms) {
			toast.error("You must accept the terms and conditions");
			return;
		}
		
		setIsSubmitting(true);
		
		try {
			// Prepare the registration data
			const registrationData = {
				organization: {
					...organizationForm
				},
				user: {
					firstName: userForm.firstName,
					lastName: userForm.lastName,
					email: userForm.email,
					password: userForm.password
				}
			};
			
			// Make the registration request to the edge function
			const response = await fetch(`${SUPABASE_URL}/functions/v1/register-to-digger`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(registrationData),
			});
			
			// Check if the request was successful
			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || "Registration failed");
			}
			
			// Registration successful
			toast.success("Registration successful! Please check your email to verify your account.");
			
			// Then in theory all is good, we can log in the user & redirect them to the dashboard
			const { data, error } = await supabase.auth.signInWithPassword({
				email: userForm.email,
				password: userForm.password,
			});

			if (error) {
				toast.error("Login failed. Please try again.");
				return;
			}

			if (data.user) {
				// Redirect to the dashboard or home page
				navigate("/");
			}
			
		} catch (error) {
			console.error("Registration error:", error);
			toast.error(error instanceof Error ? error.message : "Registration failed. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};
	
	// Render the appropriate step
	const renderStep = () => {
		switch (currentStep) {
			case RegistrationStep.OrganizationDetails:
				return (
					<>
						<CardHeader className="text-center pb-6">
							<CardTitle className="text-2xl">{t("register.organization.title") || "Organization Details"}</CardTitle>
							<CardDescription>
								{t("register.organization.description") || "Tell us about your company"}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="name">{t("register.organization.name") || "Organization Name"} *</Label>
								<Input
									id="name"
									name="name"
									value={organizationForm.name}
									onChange={handleOrgChange}
									placeholder={t("register.organization.namePlaceholder") || "Enter your organization name"}
									required
								/>
							</div>
							
							<div className="space-y-2">
								<Label htmlFor="vatNumber">{t("register.organization.vatNumber") || "VAT Number"}</Label>
								<Input
									id="vatNumber"
									name="vatNumber"
									value={organizationForm.vatNumber}
									onChange={handleOrgChange}
									placeholder={t("register.organization.vatNumberPlaceholder") || "Enter VAT number"}
								/>
							</div>
							
							<div className="space-y-2">
								<Label htmlFor="invoiceEmail">{t("register.organization.invoiceEmail") || "Email Address for Invoicing"} *</Label>
								<Input
									id="invoiceEmail"
									name="invoiceEmail"
									type="email"
									value={organizationForm.invoiceEmail}
									onChange={handleOrgChange}
									placeholder={t("register.organization.invoiceEmailPlaceholder") || "Enter email for invoicing"}
									required
								/>
							</div>
							
							<div className="border-t pt-4 mt-6">
								<h3 className="text-md font-medium mb-4">{t("register.organization.addressTitle") || "Address for Invoicing"}</h3>
								
								<div className="space-y-2">
									<Label htmlFor="attention">{t("register.organization.attention") || "Attention"}</Label>
									<Input
										id="attention"
										name="attention"
										value={organizationForm.attention}
										onChange={handleOrgChange}
										placeholder={t("register.organization.attentionPlaceholder") || "Enter attention (defaults to organization name)"}
									/>
								</div>
								
								<div className="space-y-2 mt-4">
									<Label htmlFor="street">{t("register.organization.street") || "Street"} *</Label>
									<Input
										id="street"
										name="street"
										value={organizationForm.street}
										onChange={handleOrgChange}
										placeholder={t("register.organization.streetPlaceholder") || "Enter street address"}
										required
									/>
								</div>
								
								<div className="grid grid-cols-2 gap-4 mt-4">
									<div className="space-y-2">
										<Label htmlFor="city">{t("register.organization.city") || "City"} *</Label>
										<Input
											id="city"
											name="city"
											value={organizationForm.city}
											onChange={handleOrgChange}
											placeholder={t("register.organization.cityPlaceholder") || "Enter city"}
											required
										/>
									</div>
									
									<div className="space-y-2">
										<Label htmlFor="zip">{t("register.organization.zip") || "ZIP"} *</Label>
										<Input
											id="zip"
											name="zip"
											value={organizationForm.zip}
											onChange={handleOrgChange}
											placeholder={t("register.organization.zipPlaceholder") || "Enter ZIP code"}
											required
										/>
									</div>
								</div>
								
								<div className="space-y-2 mt-4">
									<Label htmlFor="country">{t("register.organization.country") || "Country"}</Label>
									<Select 
										defaultValue="Belgium"
										onValueChange={handleCountryChange}
									>
										<SelectTrigger>
											<SelectValue placeholder={t("register.organization.countryPlaceholder") || "Select a country"} />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="Belgium">Belgium</SelectItem>
											<SelectItem value="France">France</SelectItem>
											<SelectItem value="Germany">Germany</SelectItem>
											<SelectItem value="Netherlands">Netherlands</SelectItem>
											<SelectItem value="Luxembourg">Luxembourg</SelectItem>
											<SelectItem value="United Kingdom">United Kingdom</SelectItem>
											<SelectItem value="Other">Other</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
						</CardContent>
						<CardFooter className="flex justify-end">
							<Button onClick={handleNextStep} className="flex items-center gap-2">
								{t("register.nextStep") || "Next Step"}
								<ChevronRight className="h-4 w-4" />
							</Button>
						</CardFooter>
					</>
				);
				
			case RegistrationStep.Pricing:
				return (
					<>
						<CardHeader className="text-center pb-6">
							<CardTitle className="text-2xl">{t("register.pricing.title") || "Subscription Plan"}</CardTitle>
							<CardDescription>
								{t("register.pricing.description") || "Choose the best plan for your needs"}
							</CardDescription>
							<div className="flex justify-center mt-6">
								<ToggleGroup type="single" value={billingInterval} onValueChange={v => v && setBillingInterval(v as 'month' | 'year')} className="bg-gray-100 rounded-lg p-1">
									<ToggleGroupItem value="month" className={`px-4 py-1 rounded-md text-base font-medium ${billingInterval === 'month' ? 'bg-digger-blue text-white shadow' : 'text-digger-blue'}`}>{t('register.pricing.monthly') || 'Monthly'}</ToggleGroupItem>
									<ToggleGroupItem value="year" className={`px-4 py-1 rounded-md text-base font-medium ${billingInterval === 'year' ? 'bg-digger-blue text-white shadow' : 'text-digger-blue'}`}>{t('register.pricing.yearly') || 'Yearly'}</ToggleGroupItem>
								</ToggleGroup>
							</div>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="bg-green-50 p-4 rounded-md border border-green-200 text-center mb-8">
								<h3 className="text-xl font-medium text-green-800 mb-2">
									{t("register.pricing.earlyAccess")}
								</h3>
								<p className="text-green-700">
									{t("register.pricing.freeMessage")}
								</p>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{/* Tier 1 Card */}
								<div
									className={`relative border rounded-xl p-6 transition-all duration-200 cursor-pointer bg-white hover:shadow-lg ${selectedPlan === 'tier1' ? 'border-digger-blue shadow-xl ring-2 ring-digger-blue/30' : 'border-gray-200'} group`}
									onClick={() => setSelectedPlan('tier1')}
									tabIndex={0}
									role="button"
									aria-pressed={selectedPlan === 'tier1'}
								>
									{selectedPlan === 'tier1' && (
										<div className="absolute top-4 right-4 bg-green-500 text-white rounded-full p-1 shadow-lg z-10">
											<svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M7.5 13.5l-3-3 1.06-1.06L7.5 11.38l6.44-6.44 1.06 1.06-7.5 7.5z" fill="currentColor"/></svg>
										</div>
									)}
									<div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium shadow">
										{t("register.pricing.tier1.popular")}
									</div>
									<h3 className="text-xl font-bold mb-2">{t("register.pricing.tier1.title")}</h3>
									<p className="text-gray-600 mb-4">{t("register.pricing.tier1.subtitle")}</p>
									{billingInterval === 'month' ? (
										<div className="mb-2 flex items-end gap-2">
											<span className="text-3xl font-extrabold">€119</span>
											<span className="text-gray-500 ml-1">/mo ({t('register.pricing.perUser')})</span>
										</div>
									) : (
										<>
											<div className="mb-2 flex items-end gap-2">
												<span className="text-3xl font-extrabold">€99</span>
												<span className="text-gray-500 ml-1">/mo ({t('register.pricing.perUser')})</span>
											</div>
											<div className="text-sm text-gray-500 mb-2">{t('register.pricing.billedAnnually', { price: '€1188' })}</div>
										</>
									)}
									<ul className="space-y-2 mt-4 mb-2">
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier1.feature1")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier1.feature2")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier1.feature3")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier1.feature4")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier1.feature5")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier1.feature6")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier1.feature7")}</span></li>
									</ul>
								</div>
								{/* Tier 2 Card */}
								<div
									className={`relative border rounded-xl p-6 transition-all duration-200 cursor-pointer bg-white hover:shadow-lg ${selectedPlan === 'tier2' ? 'border-digger-blue shadow-xl ring-2 ring-digger-blue/30' : 'border-gray-200'} group`}
									onClick={() => setSelectedPlan('tier2')}
									tabIndex={0}
									role="button"
									aria-pressed={selectedPlan === 'tier2'}
								>
									{selectedPlan === 'tier2' && (
										<div className="absolute top-4 right-4 bg-green-500 text-white rounded-full p-1 shadow-lg z-10">
											<svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M7.5 13.5l-3-3 1.06-1.06L7.5 11.38l6.44-6.44 1.06 1.06-7.5 7.5z" fill="currentColor"/></svg>
										</div>
									)}
									<h3 className="text-xl font-bold mb-2">{t("register.pricing.tier2.title")}</h3>
									<p className="text-gray-600 mb-4">{t("register.pricing.tier2.subtitle")}</p>
									{billingInterval === 'month' ? (
										<div className="mb-2 flex items-end gap-2">
											<span className="text-3xl font-extrabold">€239</span>
											<span className="text-gray-500 ml-1">/mo ({t('register.pricing.perUser')})</span>
										</div>
									) : (
										<>
											<div className="mb-2 flex items-end gap-2">
												<span className="text-3xl font-extrabold">€189</span>
												<span className="text-gray-500 ml-1">/mo ({t('register.pricing.perUser')})</span>
											</div>
											<div className="text-sm text-gray-500 mb-2">{t('register.pricing.billedAnnually', { price: '€2268' })}</div>
										</>
									)}
									<ul className="space-y-2 mt-4 mb-2">
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier2.feature1")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier2.feature2")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier2.feature3")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier2.feature4")}</span></li>
										<li className="flex items-center"><Check className="h-4 w-4 text-blue-500 mr-2" /><span>{t("register.pricing.tier2.feature5")}</span></li>
									</ul>
								</div>
							</div>
						</CardContent>
						<CardFooter>
							<div className="flex justify-between w-full">
								<Button variant="outline" onClick={handlePrevStep} className="flex items-center gap-2">
									<ChevronLeft className="h-4 w-4" />
									{t("register.previousStep") || "Previous"}
								</Button>
								<Button onClick={handleNextStep} className="flex items-center gap-2">
									{t("register.nextStep") || "Next Step"}
									<ChevronRight className="h-4 w-4" />
								</Button>
							</div>
						</CardFooter>
					</>
				);
				
			case RegistrationStep.UserDetails:
				return (
					<>
						<CardHeader className="text-center pb-6">
							<CardTitle className="text-2xl">{t("register.user.title") || "User Information"}</CardTitle>
							<CardDescription>
								{t("register.user.description") || "Create your account"}
							</CardDescription>
						</CardHeader>
						<form onSubmit={handleSubmit}>
							<CardContent className="space-y-4">
								<div className="grid grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label htmlFor="firstName">{t("register.user.firstName") || "First Name"} *</Label>
										<Input
											id="firstName"
											name="firstName"
											value={userForm.firstName}
											onChange={handleUserChange}
											placeholder={t("register.user.firstNamePlaceholder") || "Enter your first name"}
											required
										/>
									</div>
									
									<div className="space-y-2">
										<Label htmlFor="lastName">{t("register.user.lastName") || "Last Name"} *</Label>
										<Input
											id="lastName"
											name="lastName"
											value={userForm.lastName}
											onChange={handleUserChange}
											placeholder={t("register.user.lastNamePlaceholder") || "Enter your last name"}
											required
										/>
									</div>
								</div>
								
								<div className="space-y-2">
									<Label htmlFor="email">{t("register.user.email") || "Email Address"} *</Label>
									<Input
										id="email"
										name="email"
										type="email"
										value={userForm.email}
										onChange={handleUserChange}
										placeholder={t("register.user.emailPlaceholder") || "Enter your email address"}
										required
									/>
								</div>
								
								<div className="space-y-2">
									<Label htmlFor="password">{t("register.user.password") || "Password"} *</Label>
									<div className="relative">
										<Input
											id="password"
											name="password"
											type={showPassword ? "text" : "password"}
											value={userForm.password}
											onChange={handleUserChange}
											placeholder={t("register.user.passwordPlaceholder") || "Create a password"}
											required
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
											onClick={togglePasswordVisibility}
										>
											{showPassword ?
												<EyeOff className="h-4 w-4" /> :
												<Eye className="h-4 w-4" />
											}
										</Button>
									</div>
								</div>
								<div className="space-y-2">
									<Label htmlFor="confirmPassword">{t("register.user.confirmPassword") || "Confirm Password"} *</Label>
									<Input
										id="confirmPassword"
										name="confirmPassword"
										type={showPassword ? "text" : "password"}
										value={userForm.confirmPassword}
										onChange={handleUserChange}
										placeholder={t("register.user.confirmPasswordPlaceholder") || "Confirm your password"}
										required
									/>
									{userForm.confirmPassword && !passwordsMatch && (
										<p className="text-sm text-red-500 mt-1">
											{t("register.user.passwordsDoNotMatch") || "Passwords do not match"}
										</p>
									)}
								</div>
								<div className="text-sm mt-2">
									<p className="mb-1">{t("register.user.passwordRequirements") || "Password requirements:"}</p>
									<ul className="space-y-1 list-disc list-inside text-muted-foreground">
										<li className={hasLowercase ? "text-green-500" : ""}>
											{t("register.user.passwordLowercase") || "At least one lowercase letter"}
										</li>
										<li className={hasUppercase ? "text-green-500" : ""}>
											{t("register.user.passwordUppercase") || "At least one uppercase letter"}
										</li>
										<li className={hasDigit ? "text-green-500" : ""}>
											{t("register.user.passwordNumber") || "At least one number"}
										</li>
										<li className={hasMinLength ? "text-green-500" : ""}>
											{t("register.user.passwordMinLength") || "At least 6 characters"}
										</li>
									</ul>
								</div>
								
								<div className="flex items-center space-x-2 pt-4">
									<Checkbox 
										id="acceptTerms" 
										checked={userForm.acceptTerms}
										onCheckedChange={handleTermsChange}
										required
									/>
									<Label htmlFor="acceptTerms" className="text-sm">
										{t("register.user.acceptTerms") || "I accept the"}{" "}
										<a 
											href="https://digger-ai.be/terms" 
											target="_blank" 
											rel="noreferrer"
											className="text-blue-600 hover:underline"
										>
											{t("register.user.termsLink") || "Terms and Conditions"}
										</a>
									</Label>
								</div>
							</CardContent>
							<CardFooter>
								<div className="flex justify-between w-full">
									<Button type="button" variant="outline" onClick={handlePrevStep} className="flex items-center gap-2">
										<ChevronLeft className="h-4 w-4" />
										{t("register.previousStep") || "Previous"}
									</Button>
									<Button 
										type="submit" 
										disabled={isSubmitting || !isPasswordValid || !userForm.acceptTerms || !passwordsMatch}
										className="flex items-center gap-2"
									>
										{isSubmitting ? 
											(t("register.submitting") || "Submitting...") : 
											(t("register.submit") || "Register")
										}
										<Save className="h-4 w-4 ml-2" />
									</Button>
								</div>
							</CardFooter>
						</form>
					</>
				);
		}
	};
	
	return (
		<div className="min-h-screen w-full bg-gradient-to-br from-[#0b0c23] via-[#1a237e] to-[#0b0c23] py-24 px-2 flex flex-col items-center justify-center">
			<div className="absolute top-4 right-4 z-10">
				<LanguageSelector />
			</div>

			<div className="w-full max-w-3xl">
				<div className="card-glass shadow-2xl border border-white/30 dark:border-white/10 p-0">
					<div className="flex justify-center py-8">
						<div className="flex items-center">
							<div className="bg-digger-blue-light/80 glass rounded-2xl w-20 h-20 flex items-center justify-center mr-4 shadow-lg">
								<img
									src="/lovable-uploads/1f16b574-2585-4f89-a895-9af7f8c1fa72.png"
									alt="digger logo icon"
									className="w-12 h-12 drop-shadow-lg"
								/>
							</div>
							<span className="text-digger-blue text-4xl font-extrabold tracking-tight drop-shadow-sm">Digger</span>
						</div>
					</div>

					<div className="flex justify-center mb-6">
						<div className="glass inline-flex items-center px-6 py-2 bg-blue-100/60 dark:bg-blue-900/40 text-digger-blue rounded-full shadow-md border border-blue-200/40 dark:border-blue-900/30">
							<Sparkles className="h-5 w-5 mr-2 text-yellow-400 drop-shadow" />
							<span className="font-semibold tracking-wide text-base">{t("register.welcomeBanner") || "WELCOME TO DIGGER"}</span>
							<Sparkles className="h-5 w-5 ml-2 text-yellow-400 drop-shadow" />
						</div>
					</div>

					<div className="text-center mb-8 px-10">
						<p className="text-lg text-gray-700 dark:text-gray-200 font-medium">
							{t("register.welcomeMessage") || "We're excited to have you join us! Let's get your account set up and start finding perfect matches."}
						</p>
					</div>

					<div className="w-full px-6 mb-8">
						<div className="flex items-center justify-between">
							{[
								{ step: RegistrationStep.OrganizationDetails, label: t("register.steps.organization") || "Organization" },
								{ step: RegistrationStep.Pricing, label: t("register.steps.pricing") || "Billing Information" },
								{ step: RegistrationStep.UserDetails, label: t("register.steps.user") || "User" }
							].map((item, index, arr) => (
								<div key={index} className="flex flex-col items-center flex-1">
									<div className="flex items-center w-full">
										{index > 0 && (
											<div className="flex-1 h-1 bg-gray-200" />
										)}
										<div className={`flex items-center justify-center rounded-full z-10 transition-all duration-200
											${currentStep === item.step ? "bg-[#172554] text-white" : "bg-gray-200 text-gray-600"}
											w-16 h-16 text-2xl font-semibold border-0`}
										>
											{index + 1}
										</div>
										{index < arr.length - 1 && (
											<div className="flex-1 h-1 bg-gray-200" />
										)}
									</div>
									<div className="mt-4 min-h-[48px] flex items-center justify-center text-base text-gray-600 font-medium text-center w-32">
										{item.label}
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="px-2 md:px-8 pb-8">
						<Card className="glass shadow-lg border border-white/20 dark:border-white/10 p-0">
							{renderStep()}
						</Card>
					</div>
				</div>

				<div className="text-center mt-8 text-base text-gray-500 dark:text-gray-400">
					{t("register.alreadyHaveAccount") || "Already have an account?"} {" "}
					<a href="/auth" className="text-digger-blue font-semibold hover:underline transition-colors">
						{t("register.signIn") || "Sign in"}
					</a>
				</div>
			</div>
		</div>
	);
};

export default RegisterPage;